import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';

/**
 * `app-jems-select` — seletor JEMS3 SEM `mat-select`, análogo a `status-select`
 * e `select-input-group`. Botão-trigger próprio + `app-popover` + primitivo
 * `jemsListbox` pro conteúdo. Same look nativo macOS (círculo cinza + chevron
 * duplo), zero luta com o `display: inline-table` legado do Material.
 *
 * Piloto na página "Informações do evento" (campo Tipo). Depois expande p/
 * cobrir os `@Input` do `app-select-input` (filterable, multiple, statusChip).
 */
@Component({
  selector: 'app-jems-select',
  templateUrl: './jems-select.component.html',
  styleUrls: ['./jems-select.component.scss'],
})
export class JemsSelectComponent implements OnInit, OnChanges, OnDestroy {
  @Input() formGroupController: FormGroup;
  @Input() name: string;
  @Input() options: any[] = [];
  @Input() indices = { internal: 'id', public: 'value', image: 'flagUrl', name: 'name', offset: 'offset', filter: 'filter' };
  @Input() placeholder = '';
  @Input() description?: string;
  @Input() required = false;
  @Input() disabled = false;
  /** Prefixo aplicado ao texto público de cada opção antes do `translate`
   *  (mesma semântica do `SelectInputComponent`). */
  @Input() prependPublic = '';
  @Input() includeNoneOption = false;

  /** Dimensiona a caixa (trigger) para a opção MAIS LARGA, não para o valor
   *  selecionado: um sizer oculto empilha todas as labels e a mais larga define a
   *  largura (robusto a i18n, sem px fixo). Use com um container que encolhe ao
   *  conteúdo (ex.: coluna Bulma `is-narrow`). Só texto — não considera imagens. */
  @Input() autoWidth = false;

  /** Habilita a caixa de busca no topo do menu. */
  @Input() filterable = false;
  /** Chave de `indices` usada como campo pesquisável (default = `public`).
   *  Ex.: no select de Fuso horário o campo é `filter` (formato pré-normalizado). */
  @Input() indicesFilter = 'public';

  @Output() selectionChange = new EventEmitter<any>();

  /** Termo de busca digitado no filtro (valor cru do input). */
  public searchTerm = '';

  /** Opções após aplicar o filtro — reativas ao `searchTerm` e `options`. */
  get filteredOptions(): any[] {
    const term = (this.searchTerm || '').trim().toLowerCase();
    if (!this.filterable || !term) { return this.options || []; }
    const key = this.indices[this.indicesFilter] || this.indices.public;
    return (this.options || []).filter(o => {
      const v = o?.[key];
      return v != null && String(v).toLowerCase().includes(term);
    });
  }

  /** Snapshot exibido no trigger. Recalculado toda vez que o form muda de
   *  valor (ngOnChanges via ngOnInit) ou o usuário escolhe uma opção. */
  public display = '';

  /** Largura MÍNIMA do menu, casada à largura do trigger no `(opened)`. */
  public menuMinWidth = 0;

  @ViewChild('trigger') triggerRef!: ElementRef<HTMLElement>;
  @ViewChild('filterInput') filterInputRef?: ElementRef<HTMLInputElement>;

  /** Chamado pelo `<app-popover (opened)>` — mede o trigger e propaga a
   *  largura pro menu, garantindo que o painel tenha AO MENOS o mesmo
   *  tamanho da caixa (fica maior só se o conteúdo exigir). Se `filterable`,
   *  foca o input do filtro pra o usuário digitar direto. */
  onOpened(): void {
    const w = this.triggerRef?.nativeElement?.offsetWidth;
    if (w) { this.menuMinWidth = w; }
    if (this.filterable) {
      // Espera 1 frame: o popover ainda está montando o painel no CDK overlay.
      requestAnimationFrame(() => this.filterInputRef?.nativeElement?.focus());
    }
  }

  /** Reset do termo quando o popover fecha — próxima abertura começa limpa. */
  onClosed(): void { this.searchTerm = ''; }

  /** Sub p/ manter o `display` do trigger sincronizado quando o valor do
   *  form control muda EXTERNAMENTE (ex.: outro handler dispara `setValue`).
   *  Sem isso, o texto do trigger fica preso no snapshot inicial. */
  private valueSub?: Subscription;

  ngOnInit(): void {
    this.refreshDisplay();
    const ctrl = this.formGroupController?.get(this.name);
    this.valueSub = ctrl?.valueChanges.subscribe(() => this.refreshDisplay());
  }
  ngOnChanges(): void { this.refreshDisplay(); }
  ngOnDestroy(): void { this.valueSub?.unsubscribe(); }

  get currentId(): any {
    return this.formGroupController?.get(this.name)?.value ?? null;
  }

  /** Opção completa correspondente ao valor atual do form control — útil pro
   *  trigger quando precisa mostrar imagem (bandeira, ícone) além do label. */
  get selectedOption(): any {
    const v = this.currentId;
    if (v == null) { return null; }
    return (this.options || []).find(o => o[this.indices.internal] === v) || null;
  }

  private refreshDisplay(): void {
    const found = this.selectedOption;
    this.display = found ? `${this.prependPublic}${found[this.indices.public]}` : '';
  }

  onSelect(value: any): void {
    this.formGroupController?.get(this.name)?.setValue(value);
    this.formGroupController?.get(this.name)?.markAsDirty();
    this.refreshDisplay();
    this.selectionChange.emit(value);
  }

  /** Helper pro template — chave i18n do public value + prefixo. */
  optionLabel(o: any): string {
    return `${this.prependPublic}${o[this.indices.public]}`;
  }
}
